import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useLayoutEffect,
} from "react";
import { AUTH_STORAGE_KEYS } from "../constants/authStorageKeys";
import { clearOsStateForUser, removeLegacyOsStateKey } from "../constants/osStorageKeys";
import { clearSessionWindows } from "../utils/osSessionWindows";
import { workosAuth, MfaRequiredError } from "../services/api/workos";
import {
  armSessionIgnoreAfterLogout,
  clearSessionIgnoreAfterLogout,
  getOAuthAuthorizationCodeFromLocation,
  shouldIgnoreStaleCookieSession,
} from "../utils/sessionLogoutGuard";
import {
  ensureLogoutTrace,
  logoutInitTrace,
  logoutTrace,
} from "../utils/logoutTrace";
import {
  mapWorkosUserProfile,
  type WorkosUserProfileLike,
} from "../utils/mapWorkosUserProfile";
import type { WorkspaceWelcomeScreenConfig } from "@/types/workspaceWelcome";
import { tryCursorDevAutoLogin } from "@/utils/cursorDevAutoLogin";
import {
  clearSessionScopedLocalStorage,
  SESSION_SCOPED_LOCAL_STORAGE_KEYS,
} from "@/utils/clearSessionLocalStorage";
import {
  clearWorkosUserCache,
  loadValidatedWorkosUserCache,
} from "@/utils/workosUserCache";

interface User {
  id: string;
  email: string;
  name: string;
  fullname?: string;
  image?: string;
  company_id: string;
  company_name?: string;
  role: string;
  permissions: string[];
  modules: string[];
  subscription_tier: string | null;
  created_at: string | null;
  updated_at: string | null;
  last_login: string | null;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  email_verified?: boolean;
  profile_picture_url?: string;
  sso_enabled?: boolean;
  welcome_screen?: WorkspaceWelcomeScreenConfig | null;
}

function mapProfileToUser(profile: WorkosUserProfileLike): User {
  return mapWorkosUserProfile(profile);
}

interface AuthContextType {
  isAuthenticated: boolean;
  /** True when credentials are verified for WebSocket handshakes (JWT or live WorkOS session). */
  webSocketAuthReady: boolean;
  isLoading: boolean;
  error: string | null;
  user: User | null;
  loginWithGoogle: (email?: string) => Promise<void>;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  completeMfaLogin: (email: string, code: string, pendingAuthenticationToken: string, authenticationChallengeId: string) => Promise<void>;
  checkSSO: (email: string) => Promise<{ sso_enabled: boolean; sso_provider?: string; organization_name?: string }>;
  handleWorkOSCallback: (code: string, state: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  webSocketAuthReady: false,
  isLoading: false,
  error: null,
  user: null,
  loginWithGoogle: async () => { },
  loginWithPassword: async () => { },
  completeMfaLogin: async () => { },
  checkSSO: async () => ({ sso_enabled: false }),
  handleWorkOSCallback: async () => { },
  logout: async () => { },
  refreshUser: async () => { },
});

// Helper to check if we're in a browser environment
const isBrowser = typeof window !== "undefined";

// Safe localStorage access functions (only for user profile caching)
const safeGetItem = (key: string): string | null => {
  if (!isBrowser) return null;
  try {
    return localStorage.getItem(key);
  } catch (e) {
    console.error("localStorage.getItem failed:", e);
    return null;
  }
};

const safeSetItem = (key: string, value: string): void => {
  if (!isBrowser) return;
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    console.error("localStorage.setItem failed:", e);
  }
};

const safeRemoveItem = (key: string): void => {
  if (!isBrowser) return;
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.error("localStorage.removeItem failed:", e);
  }
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [webSocketAuthReady, setWebSocketAuthReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Use useLayoutEffect to check authentication before first render
  useLayoutEffect(() => {
    if (!isBrowser) {
      setIsLoading(false);
      setIsInitialized(true);
      return;
    }

    const initializeAuth = async () => {
      try {
        // Check for Bearer token first (non-SSO users)
        let authDataStr = safeGetItem(AUTH_STORAGE_KEYS.AUTH_DATA);
        if (!authDataStr) {
          await tryCursorDevAutoLogin();
          authDataStr = safeGetItem(AUTH_STORAGE_KEYS.AUTH_DATA);
        }
        if (authDataStr) {
          try {
            const authData = JSON.parse(authDataStr);
            if (authData.auth_token && authData.user) {
              const bearerEmail = authData.user.email as string | undefined;
              let bearerMismatch = false;
              try {
                const cookieStatus = await workosAuth.checkAuthStatus();
                if (
                  cookieStatus.authenticated &&
                  cookieStatus.email &&
                  bearerEmail &&
                  cookieStatus.email.trim().toLowerCase() !==
                    bearerEmail.trim().toLowerCase()
                ) {
                  bearerMismatch = true;
                }
              } catch {
                // Bearer-only users may have no cookie session; keep cached bearer user.
              }

              if (bearerMismatch) {
                safeRemoveItem(AUTH_STORAGE_KEYS.AUTH_DATA);
                clearWorkosUserCache();
              } else {
                const mappedUser: User = {
                  ...authData.user,
                  subscription_tier: authData.user.subscription_tier || null,
                  created_at: authData.user.created_at || null,
                  updated_at: authData.user.updated_at || null,
                  last_login: authData.user.last_login || null,
                };
                setUser(mappedUser);
                setIsAuthenticated(true);
                setWebSocketAuthReady(true);
                setIsLoading(false);
                setIsInitialized(true);
                return;
              }
            }
          } catch {
            // Invalid auth_data, continue with session check
          }
        }

        // OAuth return (query or hash ?code=…): completing SSO — not passive cookie
        // restore. Clear post-logout guard so init does not skip SSO before callback.
        try {
          if (getOAuthAuthorizationCodeFromLocation()) {
            clearSessionIgnoreAfterLogout();
            logoutInitTrace("guard.clear", { via: "oauth_return_url" });
          }
        } catch {
          // ignore
        }

        // Stale HttpOnly cookie: backend may still report authenticated after logout.
        // Ignore cookie-based restore until TTL or successful re-login (see sessionLogoutGuard).
        if (shouldIgnoreStaleCookieSession()) {
          logoutInitTrace("init.skip_cookie_restore", {
            reason: "post_logout_guard",
          });
          safeRemoveItem(AUTH_STORAGE_KEYS.WORKOS_USER);
          setIsAuthenticated(false);
          setWebSocketAuthReady(false);
          setUser(null);
          setIsLoading(false);
          setIsInitialized(true);
          return;
        }

        // Check authentication status using WorkOS session cookies (SSO users)
        const authStatus = await workosAuth.checkAuthStatus();

        if (authStatus.authenticated) {
          let userProfile;
          let fetchedFromServer = false;
          
          // Try to fetch full user profile from server first (includes permissions)
            try {
            userProfile = await workosAuth.getCurrentUser(window.location.pathname);
            fetchedFromServer = true;
          } catch (e) {
            const errorMessage = e instanceof Error ? e.message : String(e);
            
            // If server explicitly returns 401 "Not authenticated", the session is invalid
            // Do NOT fall back to cache - this prevents login loops where checkAuthStatus
            // returns authenticated:true but the actual session is expired/invalid
            if (errorMessage === 'Not authenticated') {
              safeRemoveItem(AUTH_STORAGE_KEYS.WORKOS_USER);
              setIsAuthenticated(false);
              setWebSocketAuthReady(false);
              setUser(null);
              setIsLoading(false);
              setIsInitialized(true);
              return;
            }
            
            const cachedProfile = loadValidatedWorkosUserCache({
              email: authStatus.email,
            });
            if (cachedProfile) {
              userProfile = cachedProfile;
            }
          }
          
          // If we still don't have a user profile, throw error - user should re-authenticate
          if (!userProfile) {
            console.error("[AuthContext] Failed to get user profile - no server response and no cache available");
            // Clear any stale session data and force re-authentication
            safeRemoveItem(AUTH_STORAGE_KEYS.WORKOS_USER);
            setIsAuthenticated(false);
            setWebSocketAuthReady(false);
            setUser(null);
            setIsLoading(false);
            setIsInitialized(true);
            return;
          }

          // Map WorkOS user to our User interface
          const mappedUser = mapProfileToUser(userProfile);

          setUser(mappedUser);
          setIsAuthenticated(true);
          setWebSocketAuthReady(fetchedFromServer);

          // Cache user profile in localStorage for faster loading (only if fetched from server)
          if (fetchedFromServer) {
            safeSetItem(AUTH_STORAGE_KEYS.WORKOS_USER, JSON.stringify(userProfile));
          }
        } else {
          // Not authenticated, clear any cached data
          safeRemoveItem(AUTH_STORAGE_KEYS.WORKOS_USER);
          setWebSocketAuthReady(false);
        }
      } catch (error) {
        console.error("Auth initialization failed:", error);
        const errorMessage = error instanceof Error ? error.message : String(error);
        
        // If the error indicates explicit authentication failure, don't try cache fallback
        // This prevents loops where the server says "not authenticated" but we try to use stale cache
        if (errorMessage === 'Not authenticated' || errorMessage.includes('401')) {
          safeRemoveItem(AUTH_STORAGE_KEYS.WORKOS_USER);
          safeRemoveItem(AUTH_STORAGE_KEYS.AUTH_DATA);
          setIsAuthenticated(false);
          setWebSocketAuthReady(false);
          setUser(null);
        } else {
          const authStatus = await workosAuth.checkAuthStatus();
          const cachedProfile =
            !shouldIgnoreStaleCookieSession() && authStatus.authenticated
              ? loadValidatedWorkosUserCache({ email: authStatus.email })
              : null;
          if (cachedProfile) {
            setUser(mapProfileToUser(cachedProfile));
            setIsAuthenticated(true);
            setWebSocketAuthReady(false);
          } else if (authStatus.authenticated) {
            clearWorkosUserCache();
          }
        }
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, []);



  const checkSSO = useCallback(async (email: string) => {
    setError(null);
    try {
      const result = await workosAuth.checkSSO(email);
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to check SSO status");
      throw err;
    }
  }, []);

  const loginWithGoogle = useCallback(async (email?: string) => {
    setError(null);
    try {
      // User is explicitly starting OAuth; do not let a prior logout guard block the flow
      // (init may run on /login before any ?code= is present).
      clearSessionIgnoreAfterLogout();
      logoutInitTrace("guard.clear", { via: "oauth_login_start" });
      await workosAuth.login(email);
      // This will redirect to Google OAuth, so no further code runs here
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to initiate Google login");
      throw err;
    }
  }, []);

  const loginWithPassword = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    try {
      // Login with email/password - returns user data AND auth token
      const response = await workosAuth.loginWithPassword(email, password);

      // Map user to your User interface
      const mappedUser = mapProfileToUser(response);

      setUser(mappedUser);
      setIsAuthenticated(true);
      setWebSocketAuthReady(true);

      // Cache user profile
      safeSetItem(AUTH_STORAGE_KEYS.WORKOS_USER, JSON.stringify(mappedUser));
      clearSessionIgnoreAfterLogout();
      logoutInitTrace("guard.clear", { via: "password_login" });
    } catch (err) {
      if (err instanceof MfaRequiredError) {
        throw err;
      }
      setError(err instanceof Error ? err.message : "Failed to authenticate with email/password");
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const completeMfaLogin = useCallback(async (
    email: string,
    code: string,
    pendingAuthenticationToken: string,
    authenticationChallengeId: string,
  ) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await workosAuth.verifyMfa({
        email,
        code,
        pendingAuthenticationToken,
        authenticationChallengeId,
      });

      const mappedUser = mapProfileToUser(response);

      setUser(mappedUser);
      setIsAuthenticated(true);
      setWebSocketAuthReady(true);
      safeSetItem(AUTH_STORAGE_KEYS.WORKOS_USER, JSON.stringify(mappedUser));
      clearSessionIgnoreAfterLogout();
      logoutInitTrace("guard.clear", { via: "password_login" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to verify authentication code");
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleWorkOSCallback = useCallback(async (code: string, state: string) => {
    setIsLoading(true);
    setError(null);
    try {
      // Handle the OAuth callback through WorkOS
      const userProfile = await workosAuth.handleCallback(code, state);

      // Map WorkOS user to your User interface
      const mappedUser = mapProfileToUser(userProfile);

      setUser(mappedUser);
      setIsAuthenticated(true);
      setWebSocketAuthReady(true);

      // Cache user profile
      safeSetItem(AUTH_STORAGE_KEYS.WORKOS_USER, JSON.stringify(userProfile));
      clearSessionIgnoreAfterLogout();
      logoutInitTrace("guard.clear", { via: "oauth_callback" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to authenticate with Google");
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      // First check if session is still valid
      const authStatus = await workosAuth.checkAuthStatus();
      if (!authStatus.authenticated) {
        // Session expired
        await logout();
        return;
      }

      // Try to fetch fresh user data from server (includes permissions)
      try {
        const userProfile = await workosAuth.getCurrentUser(window.location.pathname);

        const mappedUser = mapProfileToUser(userProfile);

        setUser(mappedUser);
        setWebSocketAuthReady(true);
        safeSetItem(AUTH_STORAGE_KEYS.WORKOS_USER, JSON.stringify(userProfile));
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : String(e);
        
        // If server explicitly returns 401, session is invalid - trigger logout
        if (errorMessage === 'Not authenticated') {
          await logout();
          return;
        }

        // Non-auth error - keep current user data
      }
    } catch (err) {
      console.error("Failed to refresh user:", err);
    }
    // Note: logout is intentionally not in deps to avoid circular dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const logout = useCallback(async () => {
    const logoutUserId = user?.id;
    ensureLogoutTrace();
    logoutTrace("context.logout.enter");
    // Arm before any async work or setState so a fast reload/navigation cannot
    // restore SSO from a stale cookie while logout is in flight.
    armSessionIgnoreAfterLogout();
    logoutTrace("context.logout.guard_armed_before_network");
    setIsLoading(true);
    try {
      logoutTrace("context.logout.before_workos_service");
      await workosAuth.logout();
      logoutTrace("context.logout.after_workos_service");
    } catch (err) {
      console.error("[AuthLogout] AuthContext workosAuth.logout failed:", err);
      logoutTrace("context.logout.workos_service.error", {
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      logoutTrace("context.logout.finally.start");
      // ALWAYS clear all auth-related data regardless of server response

      logoutTrace("context.logout.localStorage.keys", {
        keys: [...SESSION_SCOPED_LOCAL_STORAGE_KEYS],
        snapshot: Object.fromEntries(
          [...SESSION_SCOPED_LOCAL_STORAGE_KEYS].map((k) => [
            k,
            !!localStorage.getItem(k),
          ]),
        ),
      });
      clearSessionScopedLocalStorage();

      if (logoutUserId) {
        clearOsStateForUser(logoutUserId);
        clearSessionWindows(logoutUserId);
      } else {
        removeLegacyOsStateKey();
      }

      logoutTrace("context.logout.localStorage.cleared", {
        auth_data: !!localStorage.getItem(AUTH_STORAGE_KEYS.AUTH_DATA),
        workos_user: !!localStorage.getItem(AUTH_STORAGE_KEYS.WORKOS_USER),
      });
      
      // Clear sessionStorage (contains permission caches, etc.)
      if (isBrowser) {
        try {
          sessionStorage.clear();
        } catch (e) {
          console.error("sessionStorage.clear failed:", e);
        }
        
        // Clear all accessible cookies (non-HttpOnly cookies)
        // The session cookie is HttpOnly and cleared by the server
        try {
          const cookies = document.cookie.split(";");
          const hostname = window.location.hostname;
          const domainVariants = ["", hostname, `.${hostname}`];
          
          // For production, also try parent domain (e.g., .ani.tech for app.ani.tech)
          if (hostname !== "localhost" && hostname.split(".").length > 2) {
            const parentDomain = hostname.split(".").slice(1).join(".");
            domainVariants.push(parentDomain, `.${parentDomain}`);
          }

          for (const cookie of cookies) {
            const cookieName = cookie.split("=")[0].trim();
            if (!cookieName) continue;

            for (const domain of domainVariants) {
              const domainStr = domain ? `;domain=${domain}` : "";
              document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/${domainStr}`;
              document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/${domainStr};secure`;
              document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/${domainStr};samesite=lax`;
              document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/${domainStr};secure;samesite=lax`;
            }
          }
        } catch (e) {
          console.error("Cookie cleanup failed:", e);
        }
      }

      setUser(null);
      setIsAuthenticated(false);
      setWebSocketAuthReady(false);
      setError(null);
      setIsLoading(false);
      logoutTrace("context.logout.state_updated", {
        isAuthenticated: false,
      });
    }
  }, [user]);

  const value = {
    user,
    isAuthenticated,
    webSocketAuthReady,
    isLoading,
    error,
    loginWithGoogle,
    loginWithPassword,
    completeMfaLogin,
    checkSSO,
    handleWorkOSCallback,
    logout,
    refreshUser,
  };

  // Don't render children until auth is initialized
  if (!isInitialized) {
    return null;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
